// src/pages/AIAssistantPage.js
import React, { useState, useRef, useEffect } from "react";
import { Card, Form, Button, Spinner, Badge } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext.js";
import { useLanguage } from "../context/LanguageContext.js";
import { useAssistants } from "../hooks/useAssistants.js";
import { auth } from "../firebase.js";

// Fallback SYSTEM_PROMPT for backward compatibility
const FALLBACK_SYSTEM_PROMPT = `ROLE
You are the Virtual Assistant for Michel, owner of It's So Clean in Auckland, NZ.

Your tone is warm, friendly, clear, and professional.
Never guess services or pricing; if unsure, state you will check and follow up.

OBJECTIVE
Automatically handle cleaning requests by:
✅ Understanding and classifying the client's request
✅ Internally extracting property details for pricing only, without repeating them
✅ Selecting pricing using the guide
✅ Requesting missing info if needed
✅ Offering to check availability only if the client is ready to book
✅ Generating clear, concise, warm replies without unnecessary filler
✅ Requesting a Google Review when clients leave positive feedback

TREE-OF-THOUGHTS PROCESS (Internal)
1️⃣ Identify service type: regular, one-off, deep/move-out, post-construction, carpet, combo.
2️⃣ Extract details (beds, baths, areas) internally for pricing selection only.
3️⃣ Apply the correct pricing from the guide.
4️⃣ Use the client's requested service wording naturally, without repetition.
5️⃣ For regular cleaning, include: "The first clean might be higher to bring everything up to standard."
6️⃣ For one-off, deep/move-out cleaning on first contact, list what's included.
7️⃣ Avoid re-listing inclusions if the client has provided detailed tasks unless critical clarification is needed.
8️⃣ Avoid unnecessary filler or repetitive phrases.
9️⃣ Do not confirm booking days unless the client confirms readiness.
🔟 When a client sends positive feedback, thank them and send your Google Review link:

"Thank you so much for your feedback, it means a lot to us! If you have a moment, would you mind sharing this as a review to help others find us? Here's the link: https://g.page/r/CcmyLZo9hrGuEAE/review"
⓫ Draft a warm, clear, professional reply aligned with your brand.

11  If the client requests a capped budget, acknowledge it, clarify that the scope depends on the condition, and confirm priorities to maximize coverage, noting it may not complete all tasks.

12 when the client doens't say how many bedroom and bathroom the house has in the first contact, ask about it to give him an estimation

REPLY STRUCTURE
Greeting:
Hi [Name],

I hope you're doing well!

Quote (with natural, non-repetitive phrasing):
Thanks for getting in touch about [service type]. For a property like yours, the cost typically ranges between [price] + GST, this is an estimation, the cost could vary depending on the place conditions and what's requested.

✅ For regular cleaning only, add when you send the quote:

The first clean might be higher to bring everything up to our high standards.

Inclusions:
✅ For move-out, deep, and one-off cleaning on first contact, include:

This service generally covers:

Dusting all surfaces and blinds

Skirting boards

Walls (spot cleaning)

Mould removal (where possible)

Full kitchen clean

Cupboards and wardrobes (inside if empty and outside)

Oven cleaning

Internal windows

Rubbish removal

Bathrooms and toilets

Vacuuming

Mopping

For Regular cleaning on first contact, include:
Dusting all surfaces

Skirting boards

Kitchen (benchtops, outside of cupboards, appliances, sink, microwave, toaster, and coffee machine)

Bathrooms and toilets

Vacuuming all floors

Mopping hard floors

Taking the rubbish out

Removing cobwebs

Acknowledgment of Client Request:
If the client has listed specific tasks, acknowledge concisely:

We can certainly assist with the areas and tasks you've mentioned.

Booking Line:
Let me know if you'd like to move forward, and we can check our schedule for availability.

Sign-off:
Cheers,
Michel S.
It's So Clean

If client sets a budget cap:
We can work within your budget, but please note that the time required depends on the condition of the property. We'll prioritise the most important areas first, and if we can't complete everything within the capped time, we'll call to update you on what we've achieved and check if you'd like us to continue.

RULES
✅ For regular cleaning, include when give the price "The first clean might be higher..."
✅ For one-off, deep, move-out: list inclusions only on first contact.
✅ Avoid unnecessary filler or repetitive phrases.
✅ Avoid repeating room specs; use "for a property like yours."
✅ Never confirm a specific day unless the client confirms readiness to book.
✅ For budget cap requests, acknowledge and clarify limitations and prioritisation.
✅ When clients leave positive feedback, send them your Google Review link:
✅ NEVER USE "-" when in the context you can use "," instead.
✅ Do not send the price of the service if the client never said how many bedrooms/bathrooms his property has, instead ask how many bedrooms/bathroom he has to give an estimation

https://g.page/r/CcmyLZo9hrGuEAE/review

SERVICE & PRICING GUIDE (Internal Use Only)
Regular Cleaning:

1 to 3 beds / 1 bath: $100–130 + GST

2 to 3 beds / 2 baths: $120–150 + GST

3 to 4 beds / 3 baths: $150–200 + GST

4+ beds or 4+ baths: Site visit required

One-off / Deep / Move-out / Post-construction Cleaning: (same pricing)

1 bed / 1 bath: $200–300 + GST

2 bed / 1 bath: $250–400 + GST

2 bed / 2 bath: $350–500 + GST

3 bed / 1 bath: $350–500 + GST

3 bed / 2 bath: $400–600 + GST

4 bed / 2 bath: $450–650 + GST

4 bed / 3 bath: $550–750 + GST

5 bed / 3 bath: $600–900 + GST

5 bed / 4 bath: $750–1050 + GST

Carpet Cleaning:

1 bed: $130–160 + GST

2 beds: $180–250 + GST

3 beds: $230–300 + GST

4 beds: $250–350 + GST

5 beds: $300–400 + GST

Hourly Rate: $55/hour + GST per person (only if requested).

EXAMPLE CORRECT RESPONSE (for a one-off clean inquiry)
Hi Shivani, I hope you're doing well!

Thanks for reaching out about a one-off clean. For a property like yours, the cost typically ranges from $350–500 + GST. This is an estimation, the cost could vary depending on the place conditions and what's requested.

This service generally covers:

Dusting all surfaces and blinds

Skirting boards

Walls (spot cleaning)

Mould removal (where possible)

Full kitchen clean

Cupboards and wardrobes (inside and outside)

Oven cleaning

Internal windows

Rubbish removal

Bathrooms and toilets

Vacuuming

Mopping

Let me know if you'd like to move forward, and we can check our schedule for availability.

Michel S, It's So Clean`;

const translations = {
  en: {
    title: "AI Assistant - Client Quotes",
    clearChat: "Clear Chat",
    welcome: "Welcome to AI Assistant!",
    welcomeDesc: "Type what the client said below, and get an instant professional response.",
    clientMessage: "Client Message:",
    aiResponse: "AI Response:",
    copyResponse: "Copy Response",
    thinking: "Thinking...",
    placeholder: "Type what the client said here...",
    send: "Send",
    hint: "Press Enter to send, Shift+Enter for new line",
    accessDenied: "Access Denied - Admin or Manager only",
    confirmClear: "Clear all messages?",
    copied: "Response copied to clipboard!",
    error: "Sorry, there was an error processing your request. Please try again."
  },
  pt: {
    title: "Assistente IA - Cotações de Clientes",
    clearChat: "Limpar Chat",
    welcome: "Bem-vindo ao Assistente IA!",
    welcomeDesc: "Digite o que o cliente disse abaixo e receba uma resposta profissional instantânea.",
    clientMessage: "Mensagem do Cliente:",
    aiResponse: "Resposta da IA:",
    copyResponse: "Copiar Resposta",
    thinking: "Pensando...",
    placeholder: "Digite o que o cliente disse aqui...",
    send: "Enviar",
    hint: "Pressione Enter para enviar, Shift+Enter para nova linha",
    accessDenied: "Acesso Negado - Apenas Admin ou Gerente",
    confirmClear: "Limpar todas as mensagens?",
    copied: "Resposta copiada para a área de transferência!",
    error: "Desculpe, houve um erro ao processar sua solicitação. Por favor, tente novamente."
  }
};

export default function AIAssistantPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const location = useLocation();
  const { assistants, getAssistantById } = useAssistants();

  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const t = translations[language] || translations.en;

  // Load assistant from navigation state or default
  useEffect(() => {
    const loadAssistant = async () => {
      if (location.state?.selectedAssistantId) {
        try {
          const assistant = await getAssistantById(location.state.selectedAssistantId);
          setSelectedAssistant(assistant);
        } catch (error) {
          console.error('Error loading selected assistant:', error);
        }
      } else if (assistants.length > 0) {
        // Select default assistant or first one
        const defaultAssistant = assistants.find(a => a.is_default) || assistants[0];
        try {
          const fullAssistant = await getAssistantById(defaultAssistant.id);
          setSelectedAssistant(fullAssistant);
        } catch (error) {
          console.error('Error loading default assistant:', error);
        }
      }
    };
    loadAssistant();
  }, [assistants, location.state, getAssistantById]);

  const handleAssistantChange = async (e) => {
    const assistantId = e.target.value;
    if (!assistantId) return;

    try {
      const fullAssistant = await getAssistantById(assistantId);
      setSelectedAssistant(fullAssistant);
      setMessages([]); // Clear chat when switching assistants
    } catch (error) {
      console.error('Error changing assistant:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-resize textarea
  const handleTextareaChange = (e) => {
    setInputMessage(e.target.value);

    // Reset height to auto to get the correct scrollHeight
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      // Set height based on content, with min and max constraints
      const newHeight = Math.min(Math.max(textareaRef.current.scrollHeight, 100), 400);
      textareaRef.current.style.height = newHeight + "px";
    }
  };

  // Check if user is authorized (Admin or Manager only)
  const isAuthorized = user?.isAdmin || user?.isClientManager;

  if (!isAuthorized) {
    return (
      <div className="py-4" style={{ padding: "20px" }}>
        <h5>{t.accessDenied}</h5>
      </div>
    );
  }

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !selectedAssistant) return;

    const userMessage = {
      role: "user",
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "100px";
    }

    setLoading(true);

    try {
      // Use selected assistant's system prompt or fallback
      const systemPrompt = selectedAssistant?.system_prompt || FALLBACK_SYSTEM_PROMPT;

      // Call backend API endpoint
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";
      const user = auth.currentUser;
      if (!user) throw new Error("User must be authenticated");
      const token = await user.getIdToken();
      const response = await fetch(`${backendUrl}/ai-assistant/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          systemPrompt: systemPrompt,
          messages: [
            ...messages.map(m => ({ role: m.role, content: m.content })),
            {
              role: "user",
              content: inputMessage
            }
          ]
        })
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || "Failed to get response");
      }

      const aiMessage = {
        role: "assistant",
        content: data.message,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error("Error calling AI Assistant:", error);
      const errorMessage = {
        role: "assistant",
        content: t.error,
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    if (window.confirm(t.confirmClear)) {
      setMessages([]);
    }
  };

  const handleCopyResponse = (content) => {
    navigator.clipboard.writeText(content);
    alert(t.copied);
  };

  return (
    <>
    <style>{`
      @media (max-width: 768px) {
        .ai-assistant-container { padding: 10px 12px !important; }
        .ai-assistant-container h3 { font-size: 1.1rem !important; }
        .ai-chat-card { height: calc(100vh - 160px) !important; }
        .ai-welcome-screen { margin-top: 40px !important; padding: 20px !important; }
        .ai-chat-footer { padding: 12px 14px !important; }
        .ai-chat-textarea { height: 60px !important; padding: 10px 12px !important; font-size: 0.9rem !important; }
        .ai-send-btn { min-width: auto !important; padding: 10px 16px !important; font-size: 0.9rem !important; }
        .ai-assistant-container .form-select { max-width: 100% !important; }
      }
    `}</style>
    <div className="ai-assistant-container" style={{
      padding: "20px 40px",
      height: "100vh",
      width: "100%",
      boxSizing: "border-box"
    }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-1" style={{ fontWeight: 600, color: "#1a1a1a" }}>
            <span style={{ fontSize: "1.5rem", marginRight: "8px" }}>🤖</span>
            {t.title}
          </h3>
          <Badge bg="primary" style={{ fontSize: "0.75rem" }}>
            {user?.displayName || user?.email}
          </Badge>
        </div>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={handleClearChat}
          style={{ borderRadius: "8px" }}
        >
          🗑️ {t.clearChat}
        </Button>
      </div>

      {/* Assistant Selector */}
      {assistants.length > 0 && (
        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 500 }}>Select Assistant:</Form.Label>
          <Form.Select
            value={selectedAssistant?.id || ''}
            onChange={handleAssistantChange}
            style={{
              borderRadius: "8px",
              border: "2px solid #e0e0e0",
              maxWidth: "400px"
            }}
          >
            <option value="">Choose an assistant...</option>
            {assistants.map(assistant => (
              <option key={assistant.id} value={assistant.id}>
                {assistant.name} {assistant.is_default ? '(Default)' : ''}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      )}

      {/* Chat Container */}
      <Card
        className="ai-chat-card"
        style={{
          height: "calc(100vh - 200px)",
          display: "flex",
          flexDirection: "column",
          border: "1px solid #e0e0e0",
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          width: "100%"
        }}
      >
        {/* Messages Area */}
        <Card.Body
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "24px",
            backgroundColor: "#f8f9fa"
          }}
        >
          {messages.length === 0 && (
            <div
              className="text-center ai-welcome-screen"
              style={{
                marginTop: "120px",
                padding: "40px",
                backgroundColor: "white",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
              }}
            >
              <div style={{ fontSize: "3rem", marginBottom: "16px" }}>💬</div>
              <h4 style={{ color: "#1a1a1a", marginBottom: "12px" }}>{t.welcome}</h4>
              <p style={{ color: "#666", maxWidth: "500px", margin: "0 auto" }}>
                {t.welcomeDesc}
              </p>
            </div>
          )}

          {messages.map((msg, index) => (
            <div
              key={index}
              className={`mb-3 ${msg.role === "user" ? "text-end" : "text-start"}`}
            >
              <div
                style={{
                  display: "inline-block",
                  maxWidth: "80%",
                  padding: "14px 18px",
                  borderRadius: msg.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                  backgroundColor: msg.role === "user"
                    ? "#0d6efd"
                    : msg.isError
                    ? "#dc3545"
                    : "white",
                  color: msg.role === "user" || msg.isError ? "white" : "#1a1a1a",
                  textAlign: "left",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  boxShadow: msg.role === "user"
                    ? "0 2px 8px rgba(13, 110, 253, 0.25)"
                    : "0 2px 8px rgba(0,0,0,0.08)",
                  border: msg.role === "assistant" && !msg.isError ? "1px solid #e0e0e0" : "none"
                }}
              >
                <div
                  style={{
                    fontSize: "0.7rem",
                    marginBottom: "6px",
                    opacity: 0.85,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px"
                  }}
                >
                  {msg.role === "user" ? t.clientMessage : t.aiResponse}
                </div>
                <div style={{ fontSize: "0.95rem", lineHeight: "1.5" }}>
                  {msg.content}
                </div>
                {msg.role === "assistant" && !msg.isError && (
                  <div className="mt-3" style={{ borderTop: "1px solid #e0e0e0", paddingTop: "10px" }}>
                    <Button
                      size="sm"
                      variant="link"
                      onClick={() => handleCopyResponse(msg.content)}
                      style={{
                        color: "#0d6efd",
                        padding: "4px 8px",
                        fontSize: "0.8rem",
                        textDecoration: "none",
                        fontWeight: 500
                      }}
                    >
                      📋 {t.copyResponse}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="text-start mb-3">
              <div
                style={{
                  display: "inline-block",
                  padding: "14px 18px",
                  borderRadius: "18px 18px 18px 4px",
                  backgroundColor: "white",
                  border: "1px solid #e0e0e0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                }}
              >
                <Spinner animation="border" size="sm" style={{ marginRight: "8px" }} />
                <span style={{ color: "#666" }}>{t.thinking}</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </Card.Body>

        {/* Input Area */}
        <Card.Footer
          className="ai-chat-footer"
          style={{
            padding: "24px 40px",
            backgroundColor: "#ffffff",
            borderTop: "2px solid #f0f0f0",
            borderRadius: "0 0 12px 12px"
          }}
        >
          <div style={{ display: "flex", gap: "16px", alignItems: "flex-end", width: "100%" }}>
            <textarea
              ref={textareaRef}
              placeholder={t.placeholder}
              value={inputMessage}
              onChange={handleTextareaChange}
              onKeyPress={handleKeyPress}
              disabled={loading}
              className="form-control ai-chat-textarea"
              style={{
                flexGrow: 1,
                resize: "none",
                borderRadius: "12px",
                border: "2px solid #e0e0e0",
                fontSize: "1rem",
                padding: "16px 20px",
                transition: "border-color 0.2s",
                outline: "none",
                height: "100px",
                overflow: "auto",
                width: "auto",
                minWidth: "0"
              }}
              onFocus={(e) => e.target.style.borderColor = "#0d6efd"}
              onBlur={(e) => e.target.style.borderColor = "#e0e0e0"}
            />
            <Button
              className="ai-send-btn"
              onClick={handleSendMessage}
              disabled={loading || !inputMessage.trim()}
              style={{
                minWidth: "140px",
                borderRadius: "12px",
                fontWeight: 600,
                fontSize: "1rem",
                backgroundColor: loading || !inputMessage.trim() ? "#6c757d" : "#0d6efd",
                border: "none",
                boxShadow: "0 4px 12px rgba(13, 110, 253, 0.3)",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
              onMouseOver={(e) => {
                if (!loading && inputMessage.trim()) {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = "0 6px 16px rgba(13, 110, 253, 0.4)";
                }
              }}
              onMouseOut={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 12px rgba(13, 110, 253, 0.3)";
              }}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t.thinking}
                </>
              ) : (
                <>
                  📤 {t.send}
                </>
              )}
            </Button>
          </div>
        </Card.Footer>
      </Card>
    </div>
    </>
  );
}
